// ⚠️  DOC-UPDATE: UI changes here may require updates to:
//     • client/public/combat-guide.html  (deck builder UI, loadouts, library)
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './DeckViewer.css';   // dv- classes for My Deck tab
import './DeckManager.css';  // dm- classes for Library tab

// ============================================================
// SHARED STYLE CONSTANTS
// ============================================================

const CARD_TYPE_STYLE = {
  attack:  { icon: '⚔️',  color: '#e74c3c', label: 'Attack'  },
  spell:   { icon: '🔮',  color: '#9b59b6', label: 'Spell'   },
  block:   { icon: '🛡️',  color: '#3498db', label: 'Block'   },
  skill:   { icon: '🌳',  color: '#27ae60', label: 'Skill'   },
  power:   { icon: '⚡',  color: '#f39c12', label: 'Power'   },
  special: { icon: '✨',  color: '#1abc9c', label: 'Special' },
  heal:    { icon: '💚',  color: '#10b981', label: 'Heal'    },
  utility: { icon: '🔧',  color: '#9ca3af', label: 'Utility' },
};

const SOURCE_LABELS = {
  class:          'Class Cards',
  equipment:      'Equipment',
  seed:           'Spells / Seeds',
  skill_tree:     'Skill Tree',
  advanced_class: 'Advanced Class',
  race:           'Race',
  alignment:      'Alignment',
  level_up:       'Level-Up Picks',
  set_bonus:      'Set Bonuses',
  junk:           'Junk',
};

// ── Card sorting (loadout builder) ──
const RARITY_RANK = { junk: 0, common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5 };
// Parse the "≈NN" damage estimate the server appends to a card's description.
function cardDamageEstimate(card) {
  const m = (card.description || '').match(/≈\s*(\d+)/);
  return m ? parseInt(m[1], 10) : -1;
}
// Spell tier tracks MP cost (Flame < Fireball < Inferno < Hellfire < Apocalypse);
// non-spells fall back to rarity so the control is still meaningful for them.
function cardTierValue(card) {
  const mp = card._spellMeta && card._spellMeta.mp;
  if (typeof mp === 'number') return 10000 + mp;
  return RARITY_RANK[card.rarity] || 0;
}
function sortCards(cards, mode) {
  if (!mode || mode === 'default') return cards;
  const arr = [...cards];
  if (mode === 'damage')    arr.sort((a, b) => cardDamageEstimate(b) - cardDamageEstimate(a));
  else if (mode === 'seed') arr.sort((a, b) => (b._seedLevel || 0) - (a._seedLevel || 0));
  else if (mode === 'tier') arr.sort((a, b) => cardTierValue(b) - cardTierValue(a));
  return arr;
}


const SOURCE_ICONS = {
  class:          '🗡️',
  equipment:      '🛡️',
  seed:           '🔮',
  skill_tree:     '🌳',
  advanced_class: '⚡',
  race:           '🧝',
  alignment:      '⚖️',
  level_up:       '🎴',
  set_bonus:      '🏷️',
  junk:           '🪨',
};

const RARITY_STYLE = {
  common:    { border: '#555',    glow: 'none' },
  uncommon:  { border: '#2ecc71', glow: '0 0 6px rgba(46,204,113,0.4)' },
  rare:      { border: '#3498db', glow: '0 0 8px rgba(52,152,219,0.5)' },
  epic:      { border: '#9b59b6', glow: '0 0 10px rgba(155,89,182,0.6)' },
  legendary: { border: '#f39c12', glow: '0 0 14px rgba(243,156,18,0.7), 0 0 28px rgba(243,156,18,0.3)' },
  junk:      { border: '#7f8c8d', glow: 'none' },
};

const ELEMENT_STYLE = {
  Fire:        { icon: '🔥', color: '#e74c3c' },
  Cold:        { icon: '❄️', color: '#3498db' },
  Ice:         { icon: '❄️', color: '#3498db' },
  Electricity: { icon: '⚡', color: '#f1c40f' },
  Lightning:   { icon: '⚡', color: '#f1c40f' },
  Acid:        { icon: '🧪', color: '#27ae60' },
  Water:       { icon: '💧', color: '#2980b9' },
  Earth:       { icon: '🪨', color: '#8B7355' },
  Air:         { icon: '🌪️', color: '#bdc3c7' },
  Sonic:       { icon: '🔊', color: '#9b59b6' },
  Dark:        { icon: '🌑', color: '#6c3483' },
  Light:       { icon: '☀️', color: '#f9e79f' },
  Holy:        { icon: '✝️', color: '#f9e79f' },
  Psionic:     { icon: '🧠', color: '#a569bd' },
  Arcane:      { icon: '💫', color: '#8e44ad' },
  Healing:     { icon: '💚', color: '#27ae60' },
};

const KEYWORD_STYLE = {
  Exhaust:    { color: '#e74c3c', bg: 'rgba(231,76,60,0.2)'  },
  Retain:     { color: '#f39c12', bg: 'rgba(243,156,18,0.2)' },
  Innate:     { color: '#2ecc71', bg: 'rgba(46,204,113,0.2)' },
  Ethereal:   { color: '#9b59b6', bg: 'rgba(155,89,182,0.2)' },
  Unplayable: { color: '#e74c3c', bg: 'rgba(231,76,60,0.15)' },
};

const FORGEABLE_SOURCES = new Set([
  'class', 'race', 'alignment', 'level_up', 'seed', 'skill_tree', 'advanced_class',
]);

// Cards the player actually owns and can permanently remove (v800). Everything
// else is generated from gear/heritage and disappears by unequipping instead.
const TRASHABLE_SOURCES = new Set(['class', 'level_up', 'seed']);

// ============================================================
// FORGE PREVIEW COMPUTATION  (mirrors CardForgeEngine.js)
// ============================================================

const FORGE_RANK_LABELS = ['I', 'II', 'III', 'IV', '★'];

const ATK_DMG_BONUS    = [0.10, 0.20, 0.35, 0.50, 0.65];
const MAG_MULT_BONUS   = [0.10, 0.20, 0.30, 0.45, 0.60];
const MAG_BASE_BONUS   = [0,    2,    4,    6,    8   ];
const BLK_MULT_BONUS   = [0.25, 0.50, 0.75, 1.00, 1.25];
const BUFF_ROUNDS_ADD  = [1,    1,    2,    3,    3   ];
const BUFF_VALUE_MULT  = [1.00, 1.15, 1.25, 1.35, 1.50];
const HEAL_MULT_TABLE  = [1.10, 1.20, 1.30, 1.45, 1.60];
const SPELL_DICE_BONUS = [1,    2,    3,    4,    5   ];
const SPELL_MP_DISC    = [0,    5,    10,   15,   20  ];
const MP_PCT_ADD       = [5,    10,   15,   20,   25  ];  // flat % points added to mpRestorePercent per rank
const POWER_SCALING_STAT_ADD   = [1,    2,    3,    4,    5    ];
const POWER_SCALAR_ADD         = [0.05, 0.10, 0.15, 0.20, 0.30 ];
const POWER_PERCENT_POINTS_ADD = [5,    10,   15,   20,   25   ];
const POWER_POISON_MULT_ADD    = [0.25, 0.50, 0.75, 1.00, 1.25 ];

// Envenom (poison coating) cards
const ENVENOM_DMG_ADD  = [1,  2,  3,  4,  6 ];
const ENVENOM_DUR_ADD  = [0,  0,  1,  1,  2 ];

// DoT (damage over time) cards
const DOT_DMG_ADD      = [1,  2,  3,  4,  5 ];

// Standalone thorns cards
const THORNS_DMG_ADD   = [2,  4,  6,  8,  10];

// Standalone regeneration cards
const REGEN_HEAL_ADD   = [1,  2,  3,  4,  6 ];

// Attack multiplier this turn cards
const ATK_MULT_TURN_ADD = [0.10, 0.15, 0.25, 0.35, 0.50];

function r2(n) { return +(Math.round(n * 100) / 100).toFixed(2); }

function statLabel(stat) {
  switch ((stat || '').toLowerCase()) {
    case 'strength': return 'STR';
    case 'agility': return 'AGL';
    case 'vitality': return 'VIT';
    case 'mentality': return 'MEN';
    case 'charisma': return 'CHA';
    case 'level': return 'level';
    default: return (stat || 'stat').toUpperCase();
  }
}

/**
 * Returns an array of { label, before, after, isUnlock } objects describing
 * exactly what changes when fc (at fc.forgeRank) is upgraded by one rank.
 */
function computeForgePreview(fc) {
  const cur = fc.forgeRank;         // 0 = unforged
  const nxt = cur + 1;
  if (nxt < 1 || nxt > 5) return [];

  const ci = cur > 0 ? cur - 1 : null;  // current table index (null if unforged)
  const ni = nxt - 1;                    // next table index
  const eff = fc.effects || {};
  const changes = [];

  const pushScalingObjectChange = (key, label) => {
    const scaling = eff[key];
    if (!scaling) return false;
    const baseStatMult = scaling._baseStatMult !== undefined ? scaling._baseStatMult : (scaling.statMult ?? 0);
    const statModLabel = `${statLabel(scaling.stat)} mod`;
    changes.push({
      label,
      before: ci !== null ? `${baseStatMult + POWER_SCALING_STAT_ADD[ci]}× ${statModLabel}` : `${baseStatMult}× ${statModLabel}`,
      after: `${baseStatMult + POWER_SCALING_STAT_ADD[ni]}× ${statModLabel}`,
    });
    return true;
  };

  if (eff.magicDamage) {
    const md = eff.magicDamage;
    const baseMult = md._baseMult !== undefined ? md._baseMult : (md.multiplier ?? 1.0);
    const baseBase = md._baseBase !== undefined ? md._baseBase : (md.base ?? 0);
    changes.push({
      label:  'Damage Multiplier ×',
      before: ci !== null ? r2(baseMult + MAG_MULT_BONUS[ci]) : r2(baseMult),
      after:  r2(baseMult + MAG_MULT_BONUS[ni]),
    });
    const prevBase = ci !== null ? baseBase + MAG_BASE_BONUS[ci] : baseBase;
    const nextBase = baseBase + MAG_BASE_BONUS[ni];
    if (nextBase !== prevBase) {
      changes.push({ label: 'Flat Base Damage', before: prevBase, after: nextBase });
    }
    if (nxt >= 4 && cur < 4) {
      changes.push({ label: 'Draw 1 after cast', before: '—', after: '✓ Unlocked', isUnlock: true });
    }

  } else if (eff.action === 'attack' || eff.damageMultiplier !== undefined) {
    const baseMult = (eff._baseMultiplier != null) ? eff._baseMultiplier : (eff.damageMultiplier ?? 1.0);
    changes.push({
      label:  'Damage ×',
      before: ci !== null ? r2(baseMult + ATK_DMG_BONUS[ci]) : r2(baseMult),
      after:  r2(baseMult + ATK_DMG_BONUS[ni]),
    });
    if (nxt >= 5 && cur < 5) {
      changes.push({
        label: 'AP Cost', before: fc.apCost,
        after: Math.max(1, fc.apCost - 1), isUnlock: true,
      });
    }

  } else if (eff.blockStat || eff.blockMultiplier !== undefined || eff.blockFlat !== undefined) {
    const baseMult = (eff._baseBlockMultiplier != null) ? eff._baseBlockMultiplier : (eff.blockMultiplier ?? 1.0);
    changes.push({
      label:  'Block ×',
      before: ci !== null ? r2(baseMult + BLK_MULT_BONUS[ci]) : r2(baseMult),
      after:  r2(baseMult + BLK_MULT_BONUS[ni]),
    });
    if (nxt >= 4 && cur < 4) {
      changes.push({ label: 'Draw 1 after block', before: '—', after: '✓ Unlocked', isUnlock: true });
    }
    if (nxt >= 5 && cur < 5) {
      changes.push({ label: 'Retain keyword', before: '—', after: '✓ Added', isUnlock: true });
    }

  } else if (eff.buff || eff.debuff) {
    const bd = eff.buff || eff.debuff;
    const baseRounds = bd._baseRounds !== undefined ? bd._baseRounds : (bd.rounds ?? 1);
    const baseValue  = bd._baseValue  !== undefined ? bd._baseValue  : (bd.value  ?? 1);
    changes.push({
      label:  'Duration (rounds)',
      before: ci !== null ? baseRounds + BUFF_ROUNDS_ADD[ci] : baseRounds,
      after:  baseRounds + BUFF_ROUNDS_ADD[ni],
    });
    const prevVal = ci !== null ? Math.ceil(baseValue * BUFF_VALUE_MULT[ci]) : baseValue;
    const nextVal = Math.ceil(baseValue * BUFF_VALUE_MULT[ni]);
    if (prevVal !== nextVal) {
      changes.push({ label: 'Effect Value', before: prevVal, after: nextVal });
    }
    if (nxt >= 5 && cur < 5) {
      changes.push({ label: 'Exhaust keyword', before: 'Exhaust', after: '✓ Removed', isUnlock: true });
    }

  } else if (eff.heal || eff.healPercent || eff.healFlat) {
    const beforeMult = ci !== null ? HEAL_MULT_TABLE[ci] : 1.0;
    const afterMult  = HEAL_MULT_TABLE[ni];
    changes.push({
      label:  'Heal Amount ×',
      before: beforeMult.toFixed(2),
      after:  afterMult.toFixed(2),
    });
    if (nxt >= 5 && cur < 5) {
      changes.push({ label: 'Retain keyword', before: '—', after: '✓ Added', isUnlock: true });
    }

  } else if (eff.mpRestorePercent !== undefined) {
    const basePct = eff._baseMpPct !== undefined ? eff._baseMpPct : eff.mpRestorePercent;
    changes.push({
      label:  'MP Restore',
      before: ci !== null ? `${basePct + MP_PCT_ADD[ci]}% of max MP` : `${basePct}% of max MP`,
      after:  `${basePct + MP_PCT_ADD[ni]}% of max MP`,
    });
    if (nxt >= 5 && cur < 5) {
      changes.push({ label: 'Exhaust (once per combat)', before: 'Active', after: '✓ Removed — reusable!', isUnlock: true });
    }

  } else if (pushScalingObjectChange('flatDamagePerHitTaken', 'Damage Per Hit Taken')) {
  } else if (pushScalingObjectChange('flatDamagePerAttackPlayed', 'Damage Per Attack Played')) {
  } else if (pushScalingObjectChange('flatDamagePerCritLanded', 'Damage Per Crit Landed')) {
  } else if (pushScalingObjectChange('flatDamagePerSpellCast', 'Damage Per Spell Cast')) {
  } else if (pushScalingObjectChange('holyDamagePerHeal', 'Holy Damage Per Heal')) {
  } else if (pushScalingObjectChange('natureDamagePerHeal', 'Nature Damage Per Heal')) {
  } else if (pushScalingObjectChange('petPermanentAtt', 'Pet ATT Gain')) {
  } else if (pushScalingObjectChange('permanentFlatDamage', 'Flat Damage')) {
  } else if (eff.permanentHiddenBonus !== undefined) {
    const baseBonus = eff._basePermanentHiddenBonus !== undefined ? eff._basePermanentHiddenBonus : eff.permanentHiddenBonus;
    changes.push({
      label: 'Hidden Damage Bonus',
      before: `${Math.round((ci !== null ? baseBonus + POWER_SCALAR_ADD[ci] : baseBonus) * 100)}%`,
      after: `${Math.round((baseBonus + POWER_SCALAR_ADD[ni]) * 100)}%`,
    });

  } else if (eff.spellDamageAmplify !== undefined) {
    const baseAmp = eff._baseSpellDamageAmplify !== undefined ? eff._baseSpellDamageAmplify : eff.spellDamageAmplify;
    changes.push({
      label: 'Spell Damage Amplify',
      before: `${Math.round((ci !== null ? baseAmp + POWER_SCALAR_ADD[ci] : baseAmp) * 100)}%`,
      after: `${Math.round((baseAmp + POWER_SCALAR_ADD[ni]) * 100)}%`,
    });

  } else if (eff.permanentResistReduction !== undefined) {
    const baseReduction = eff._basePermanentResistReduction !== undefined ? eff._basePermanentResistReduction : eff.permanentResistReduction;
    changes.push({
      label: 'Magic Resist Reduction',
      before: `${ci !== null ? baseReduction + POWER_PERCENT_POINTS_ADD[ci] : baseReduction}%`,
      after: `${baseReduction + POWER_PERCENT_POINTS_ADD[ni]}%`,
    });

  } else if (eff.flatDamagePerMpSpent !== undefined) {
    const baseRatio = eff._baseFlatDamagePerMpSpent !== undefined ? eff._baseFlatDamagePerMpSpent : eff.flatDamagePerMpSpent;
    changes.push({
      label: 'Damage Per MP Spent',
      before: `${Math.round((ci !== null ? baseRatio + POWER_SCALAR_ADD[ci] : baseRatio) * 100)}%`,
      after: `${Math.round((baseRatio + POWER_SCALAR_ADD[ni]) * 100)}%`,
    });

  } else if (eff.doublyPoison !== undefined) {
    const basePoisonMult = eff._baseDoublyPoisonMultiplier !== undefined ? eff._baseDoublyPoisonMultiplier : (typeof eff.doublyPoison === 'number' ? eff.doublyPoison : 2);
    changes.push({
      label: 'Active Poison Multiplier',
      before: `${r2(ci !== null ? basePoisonMult + POWER_POISON_MULT_ADD[ci] : basePoisonMult)}×`,
      after: `${r2(basePoisonMult + POWER_POISON_MULT_ADD[ni])}×`,
    });

  } else if (eff.envenom) {
    // Envenom (poison coating) cards
    const baseDmg = eff.envenom._baseDamage !== undefined ? eff.envenom._baseDamage : (eff.envenom.damage || 3);
    const baseDur = eff.envenom._baseDuration !== undefined ? eff.envenom._baseDuration : (eff.envenom.duration || 4);
    changes.push({
      label: 'Poison Damage / Tick',
      before: ci !== null ? baseDmg + ENVENOM_DMG_ADD[ci] : baseDmg,
      after: baseDmg + ENVENOM_DMG_ADD[ni],
    });
    const prevDur = ci !== null ? baseDur + ENVENOM_DUR_ADD[ci] : baseDur;
    const nextDur = baseDur + ENVENOM_DUR_ADD[ni];
    if (prevDur !== nextDur) {
      changes.push({ label: 'Coating Duration', before: `${prevDur} rounds`, after: `${nextDur} rounds` });
    }

  } else if (eff.dot) {
    // DoT cards
    const baseDmg = eff.dot._baseDamage !== undefined ? eff.dot._baseDamage : (eff.dot.damage || 5);
    changes.push({
      label: 'DoT Damage / Tick',
      before: ci !== null ? baseDmg + DOT_DMG_ADD[ci] : baseDmg,
      after: baseDmg + DOT_DMG_ADD[ni],
    });

  } else if (eff.thorns && typeof eff.thorns === 'object') {
    // Standalone thorns cards
    const baseDmg = eff.thorns._baseDamage !== undefined ? eff.thorns._baseDamage : (eff.thorns.damage || 0);
    changes.push({
      label: 'Thorns Damage',
      before: ci !== null ? baseDmg + THORNS_DMG_ADD[ci] : baseDmg,
      after: baseDmg + THORNS_DMG_ADD[ni],
    });

  } else if (eff.regeneration && typeof eff.regeneration === 'object') {
    // Standalone regeneration cards
    const baseHeal = eff.regeneration._baseHealPerTurn !== undefined ? eff.regeneration._baseHealPerTurn : (eff.regeneration.healPerTurn || 0);
    changes.push({
      label: 'Heal Per Turn',
      before: ci !== null ? baseHeal + REGEN_HEAL_ADD[ci] : baseHeal,
      after: baseHeal + REGEN_HEAL_ADD[ni],
    });

  } else if (eff.attackMultiplierThisTurn !== undefined) {
    // Attack multiplier this turn cards
    const baseMult = eff._baseAttackMultiplierThisTurn !== undefined ? eff._baseAttackMultiplierThisTurn : eff.attackMultiplierThisTurn;
    changes.push({
      label: 'Attack Multiplier',
      before: `${r2(ci !== null ? baseMult + ATK_MULT_TURN_ADD[ci] : baseMult)}×`,
      after: `${r2(baseMult + ATK_MULT_TURN_ADD[ni])}×`,
    });

  } else if (eff.draw !== undefined || eff.scry || eff.gainAp || eff.apGain) {
    // Draw / scry / AP utility cards
    if (eff.draw !== undefined) {
      const baseDraw = eff._baseDraw !== undefined ? eff._baseDraw : eff.draw;
      const prevDraw = ci !== null && ci >= 3 ? baseDraw + 1 : baseDraw;
      const nextDraw = ni >= 3 ? baseDraw + 1 : baseDraw;
      if (prevDraw !== nextDraw) {
        changes.push({ label: 'Cards Drawn', before: prevDraw, after: nextDraw, isUnlock: true });
      }
    }
    if (nxt >= 5 && cur < 5 && fc.apCost > 1) {
      changes.push({ label: 'AP Cost', before: fc.apCost, after: Math.max(1, fc.apCost - 1), isUnlock: true });
    }
    if (changes.length === 0) {
      // Show what rank IV unlocks at lower ranks
      if (nxt < 4 && eff.draw !== undefined) {
        changes.push({ label: 'Rank IV Bonus', before: '—', after: '+1 Draw (at Rank IV)', isUnlock: false });
      } else if (nxt < 5 && fc.apCost > 1) {
        changes.push({ label: 'Rank ★ Bonus', before: '—', after: 'AP Cost -1 (at Rank ★)', isUnlock: false });
      } else {
        changes.push({ label: 'Forge Rank', before: ci !== null ? `Rank ${FORGE_RANK_LABELS[ci]}` : 'Unranked', after: `Rank ${FORGE_RANK_LABELS[ni]}` });
      }
    }

  } else if (eff.hidden) {
    // Stealth cards
    if (nxt >= 4 && cur < 4) {
      changes.push({ label: 'Draw on Stealth', before: '—', after: '✓ Draw 1', isUnlock: true });
    }
    if (nxt >= 5 && cur < 5) {
      changes.push({ label: 'AP Cost', before: fc.apCost, after: Math.max(0, fc.apCost - 1), isUnlock: true });
    }
    if (changes.length === 0) {
      if (nxt < 4) {
        changes.push({ label: 'Rank IV Bonus', before: '—', after: 'Draw 1 on stealth (at Rank IV)', isUnlock: false });
      } else {
        changes.push({ label: 'Rank ★ Bonus', before: '—', after: 'AP Cost -1 (at Rank ★)', isUnlock: false });
      }
    }

  } else if (eff.ability) {
    // Ability cards (Boost, Whirlwind, Taunt, etc.)
    const hasExhaust = (fc.keywords || []).includes('Exhaust');
    if (nxt >= 5 && cur < 5 && fc.apCost > 1) {
      changes.push({ label: 'AP Cost', before: fc.apCost, after: Math.max(1, fc.apCost - 1), isUnlock: true });
    }
    if (nxt >= 5 && cur < 5 && hasExhaust) {
      changes.push({ label: 'Exhaust keyword', before: 'Exhaust', after: '✓ Removed', isUnlock: true });
    }
    if (changes.length === 0) {
      const milestones = [];
      if (fc.apCost > 1) milestones.push({ label: 'Rank ★ Bonus', before: '—', after: 'AP Cost -1 (at Rank ★)', isUnlock: false });
      if (hasExhaust) milestones.push({ label: 'Rank ★ Bonus', before: '—', after: 'Exhaust removed (at Rank ★)', isUnlock: false });
      if (milestones.length > 0) {
        changes.push(...milestones);
      } else {
        changes.push({ label: 'Forge Rank', before: ci !== null ? `Rank ${FORGE_RANK_LABELS[ci]}` : 'Unranked', after: `Rank ${FORGE_RANK_LABELS[ni]}` });
      }
    }

  } else if (eff.spell || fc.source === 'seed') {
    const beforeDice = ci !== null ? SPELL_DICE_BONUS[ci] : 0;
    const afterDice  = SPELL_DICE_BONUS[ni];
    changes.push({
      label:  'Bonus Spell Dice',
      before: beforeDice > 0 ? `+${beforeDice}` : '0',
      after:  `+${afterDice}`,
    });
    const beforeDisc = ci !== null ? SPELL_MP_DISC[ci] : 0;
    const afterDisc  = SPELL_MP_DISC[ni];
    if (beforeDisc !== afterDisc) {
      changes.push({ label: 'MP Discount', before: `${beforeDisc}%`, after: `${afterDisc}%` });
    }
    if (nxt >= 4 && cur < 4) {
      changes.push({ label: 'Draw 1 after cast', before: '—', after: '✓ Unlocked', isUnlock: true });
    }

  } else {
    // Generic fallback — cards with unique effects that don't have dedicated forge scaling
    const hasExhaust = (fc.keywords || []).includes('Exhaust');
    if (nxt >= 5 && cur < 5 && fc.apCost > 1) {
      changes.push({
        label: 'AP Cost', before: fc.apCost,
        after: Math.max(1, fc.apCost - 1), isUnlock: true,
      });
    }
    if (nxt >= 5 && cur < 5 && hasExhaust) {
      changes.push({ label: 'Exhaust keyword', before: 'Exhaust', after: '✓ Removed', isUnlock: true });
    }
    if (changes.length === 0) {
      // Show upcoming milestones
      const milestones = [];
      if (fc.apCost > 1) milestones.push({ label: 'Rank ★ Bonus', before: '—', after: 'AP Cost -1 (at Rank ★)', isUnlock: false });
      if (hasExhaust) milestones.push({ label: 'Rank ★ Bonus', before: '—', after: 'Exhaust removed (at Rank ★)', isUnlock: false });
      if (milestones.length > 0) {
        changes.push(...milestones);
      } else {
        changes.push({ label: 'Forge Rank', before: ci !== null ? `Rank ${FORGE_RANK_LABELS[ci]}` : 'Unranked', after: `Rank ${FORGE_RANK_LABELS[ni]}` });
      }
    }
  }

  return changes;
}

// ============================================================
// CARD COMPONENTS
// ============================================================

/** Card tile — toggleable + clickable for full detail. */
const LibraryCard = ({ card, onToggle, forgeRank, onCardClick }) => {
  const typeStyle   = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
  const rarityStyle = RARITY_STYLE[card.rarity]  || RARITY_STYLE.common;
  const element     = card._spellMeta?.element || card.effects?.magicDamage?.element;
  const elemStyle   = element ? ELEMENT_STYLE[element] : null;
  const isDisabled  = card._disabled;
  const canToggle   = card._canToggle;
  const isSetBonus  = card.source === 'set_bonus' || card.effects?._setBonus;

  return (
    <div
      className={`dm-card ${card.type} rarity-${card.rarity} ${isDisabled ? 'dm-disabled' : ''} ${isSetBonus ? 'set-bonus' : ''}`}
      style={{ borderColor: isDisabled ? '#444' : rarityStyle.border, boxShadow: isDisabled ? 'none' : rarityStyle.glow, position: 'relative', cursor: 'pointer' }}
      onClick={() => onCardClick && onCardClick(card)}
      title="Click for details"
    >
      {card.rarity === 'legendary' && !isDisabled && <div className="dm-legendary-sparkle" />}
      <div className="dm-card-ap" style={{ background: isDisabled ? '#444' : typeStyle.color }}>{card.apCost}</div>
      {forgeRank > 0 && (
        <div style={{
          position: 'absolute', top: '4px', right: '4px',
          background: isDisabled ? 'rgba(100,80,40,0.65)' : 'rgba(230,126,34,0.9)',
          borderRadius: '4px', padding: '1px 5px',
          fontSize: '0.6rem', fontWeight: 'bold', color: '#fff', zIndex: 2,
        }}>⚒{FORGE_RANK_LABELS[forgeRank - 1]}</div>
      )}
      <div className="dm-card-header">
        <span className="dm-card-type-icon">{typeStyle.icon}</span>
        <span className={`dm-card-name ${isDisabled ? 'dm-text-muted' : ''}`}>{card.name}</span>
      </div>
      {elemStyle && <div className="dm-card-element" style={{ color: isDisabled ? '#555' : elemStyle.color }}>{elemStyle.icon} {element}</div>}
      {card._spellMeta?.mp > 0 && <div className="dm-card-mp" style={{ color: isDisabled ? '#445' : '#3498db' }}>{card._spellMeta.mp} MP</div>}
      <div className={`dm-card-desc ${isDisabled ? 'dm-text-muted' : ''}`}>{card.description}</div>
      {card.keywords && card.keywords.length > 0 && (
        <div className="dm-card-keywords">
          {card.keywords.map((kw, i) => {
            const kwStyle = KEYWORD_STYLE[kw] || { color: '#aaa', bg: 'rgba(150,150,150,0.2)' };
            return (
              <span key={i} className="dm-keyword" style={{
                color: isDisabled ? '#555' : kwStyle.color,
                background: isDisabled ? 'rgba(50,50,50,0.3)' : kwStyle.bg,
              }}>{kw}</span>
            );
          })}
        </div>
      )}
      {canToggle ? (
        <button
          className={`dm-toggle-btn ${isDisabled ? 'dm-toggle-off' : 'dm-toggle-on'}`}
          onClick={e => { e.stopPropagation(); onToggle(card); }}
          title={isDisabled ? 'Enable this card' : 'Disable this card'}
        >
          {isDisabled ? '❌ Removed' : '✅ Active'}
        </button>
      ) : (card.source === 'race' || card.source === 'alignment') ? (
        <div className="dm-locked-badge dm-innate" onClick={e => e.stopPropagation()} title="Innate — scales with level">🧬 Innate{card._levelScaled ? ' (Scaled)' : ''}</div>
      ) : (card.source === 'class' || card.source === 'equipment') ? (
        <div className="dm-locked-badge dm-perk-locked" onClick={e => e.stopPropagation()} title="Unlock removal perks in your skill tree">🔒 Needs Perk</div>
      ) : (
        <div className="dm-locked-badge" onClick={e => e.stopPropagation()} title="This card cannot be removed">🔒 Locked</div>
      )}
    </div>
  );
};

// ============================================================
// CARD DETAIL MODAL  (click a card in My Deck to expand)
// ============================================================

const CardDetailModal = ({ card, onClose, onGoToForge, cardUpgrades, trashTokens = 0, onTrash, trashing = false }) => {
  const [confirmTrash, setConfirmTrash] = React.useState(false);
  React.useEffect(() => { setConfirmTrash(false); }, [card]);
  if (!card) return null;
  const typeStyle = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
  const element   = card._spellMeta?.element || card.effects?.magicDamage?.element;
  const elemStyle = element ? ELEMENT_STYLE[element] : null;
  const forgeRank = cardUpgrades?.[card.cardKey] || 0;
  const isForgeable = FORGEABLE_SOURCES.has(card.source) && !card.isJunk;
  // Only cards the player owns can be trashed. Equipment, race, alignment, set
  // bonus and skill-tree cards are generated from character state.
  const isTrashable = TRASHABLE_SOURCES.has(card.source);
  const trashKey = card.source === 'class' ? `cl:${card.cardKey}`
                 : card.source === 'level_up' ? `lv:${card.cardKey}`
                 : `sp:${card.cardKey}`;
  const isSpell = card.source === 'seed';

  return (
    <div className="dv-detail-overlay" onClick={onClose}>
      <div className="dv-detail-card" onClick={e => e.stopPropagation()}>
        <div className="dv-detail-header" style={{ borderColor: typeStyle.color }}>
          <span className="dv-detail-ap" style={{ background: typeStyle.color }}>{card.apCost} AP</span>
          <h3>{card.name}</h3>
          <span className={`dv-detail-rarity rarity-${card.rarity}`}>{card.rarity}</span>
        </div>
        <div className="dv-detail-body">
          <div className="dv-detail-type">{typeStyle.icon} {typeStyle.label}</div>
          {elemStyle && <div className="dv-detail-element">{elemStyle.icon} {element}</div>}
          {card._spellMeta?.mp > 0 && <div className="dv-detail-mp">🔮 {card._spellMeta.mp} MP</div>}
          <p className="dv-detail-desc">{card.description}</p>
          {card.keywords && card.keywords.length > 0 && (
            <div className="dv-detail-keywords">
              {card.keywords.map((kw, i) => {
                const kwStyle = KEYWORD_STYLE[kw] || { color: '#aaa', bg: 'rgba(150,150,150,0.2)' };
                return <span key={i} className="dv-keyword-lg" style={{ color: kwStyle.color, background: kwStyle.bg }}>{kw}</span>;
              })}
            </div>
          )}
          <div className="dv-detail-source">{SOURCE_ICONS[card.source]} {SOURCE_LABELS[card.source] || card.source}</div>
          {isForgeable && (
            <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: 'rgba(230,126,34,0.08)', border: '1px solid rgba(230,126,34,0.25)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem' }}>⚒</span>
                <span style={{ fontSize: '0.8rem', color: '#ddd' }}>
                  Forge Rank:&nbsp;
                  {forgeRank === 0
                    ? <span style={{ color: '#666' }}>Unforged</span>
                    : <strong style={{ color: '#e67e22' }}>{FORGE_RANK_LABELS[forgeRank - 1]}{forgeRank === 5 ? ' (MAX)' : ''}</strong>
                  }
                </span>
                {forgeRank < 5 && onGoToForge && (
                  <button onClick={() => { onClose(); onGoToForge(card.cardKey); }} style={{
                    marginLeft: 'auto', padding: '0.25rem 0.65rem',
                    background: 'linear-gradient(135deg,#e67e22,#d35400)',
                    border: 'none', borderRadius: '5px', color: '#fff',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.75rem',
                  }}>
                    ⚒ Forge →
                  </button>
                )}
              </div>
              {forgeRank > 0 && (
                <div style={{ display: 'flex', gap: '3px', marginTop: '0.4rem' }}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} style={{ width: '10px', height: '10px', borderRadius: '50%', background: i < forgeRank ? '#e67e22' : '#333', border: `1px solid ${i < forgeRank ? '#e67e22' : '#555'}` }} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {isTrashable ? (
          <div style={{ padding: '0 1rem 0.75rem' }}>
            {!confirmTrash ? (
              <button
                onClick={() => setConfirmTrash(true)}
                disabled={trashTokens < 1 || trashing}
                title={trashTokens < 1 ? 'You earn a Trash Token every 5 levels' : ''}
                style={{
                  width: '100%', padding: '0.45rem', borderRadius: '6px', cursor: trashTokens < 1 ? 'not-allowed' : 'pointer',
                  background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)',
                  color: trashTokens < 1 ? '#7a4a45' : '#e74c3c', fontSize: '0.78rem', fontWeight: 'bold',
                }}
              >
                🗑 {isSpell ? 'Unlearn Spell' : 'Trash Card'} · {trashTokens} token{trashTokens !== 1 ? 's' : ''}
              </button>
            ) : (
              <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '6px', padding: '0.6rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#ddd', marginBottom: '0.5rem' }}>
                  {isSpell
                    ? <>Permanently unlearn <strong>{card.name}</strong>? It leaves your spellbook and your deck.</>
                    : <>Permanently remove <strong>{card.name}</strong> from your library?</>}
                  {forgeRank > 0 && <> Its <strong>Rank {FORGE_RANK_LABELS[forgeRank - 1]}</strong> forge upgrade will be lost.</>}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => onTrash && onTrash(trashKey)} disabled={trashing} style={{
                    flex: 1, padding: '0.4rem', borderRadius: '5px', border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg,#c0392b,#922b21)', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem',
                  }}>{trashing ? 'Working…' : 'Confirm'}</button>
                  <button onClick={() => setConfirmTrash(false)} disabled={trashing} style={{
                    flex: 1, padding: '0.4rem', borderRadius: '5px', border: '1px solid #444', cursor: 'pointer',
                    background: 'transparent', color: '#aaa', fontSize: '0.75rem',
                  }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 0.75rem', fontSize: '0.72rem', color: '#666', textAlign: 'center' }}>
            This card comes from your gear or heritage — unequip or respec to remove it.
          </div>
        )}
        <button className="dv-detail-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

// ============================================================
// FORGE PREVIEW MODAL
// ============================================================

const ForgePreviewModal = ({ fc, forgeTokens, onConfirm, onCancel, working }) => {
  const changes   = computeForgePreview(fc);
  const typeStyle = CARD_TYPE_STYLE[fc.type] || CARD_TYPE_STYLE.special;
  const curLabel  = fc.forgeRank > 0 ? `Rank ${FORGE_RANK_LABELS[fc.forgeRank - 1]}` : 'Unforged';
  const nxtLabel  = FORGE_RANK_LABELS[fc.forgeRank];
  const baseName  = fc.name.replace(/ \[[IVX★]+\]$/, '');
  const nextName  = `${baseName} [${nxtLabel}]`;
  const canAfford = forgeTokens >= 1;
  const element   = fc._spellMeta?.element || fc.effects?.magicDamage?.element;
  const elemStyle = element ? ELEMENT_STYLE[element] : null;
  const apChange  = changes.find(c => c.label === 'AP Cost');
  const statChanges = changes.filter(c => c.label !== 'AP Cost');

  const CardPanel = ({ side }) => {
    const isCurrent = side === 'current';
    const rank      = isCurrent ? fc.forgeRank : fc.forgeRank + 1;
    const label     = isCurrent ? curLabel : (nxtLabel === '★' ? '★ MAX RANK' : `Rank ${nxtLabel}`);
    const name      = isCurrent ? fc.name : nextName;
    const ap        = apChange ? (isCurrent ? apChange.before : apChange.after) : fc.apCost;
    const apReduced = !isCurrent && !!apChange;

    return (
      <div style={{
        background: isCurrent ? 'rgba(0,0,0,0.40)' : 'rgba(230,126,34,0.07)',
        border: isCurrent ? '1px solid #2a2a3a' : '2px solid rgba(230,126,34,0.40)',
        borderRadius: '12px', padding: '0.85rem',
        display: 'flex', flexDirection: 'column', gap: '0.38rem',
      }}>
        {/* Panel label */}
        <div style={{ fontSize: '0.6rem', color: isCurrent ? '#555' : '#e67e22', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '0.05rem' }}>
          {isCurrent ? 'Current' : 'After Forge'}
        </div>

        {/* Rank dots */}
        <div style={{ display: 'flex', gap: '3px' }}>
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} style={{ width: '9px', height: '9px', borderRadius: '50%', background: i < rank ? '#e67e22' : '#1e1e1e', border: `1px solid ${i < rank ? '#e67e22' : '#3a3a3a'}` }} />
          ))}
        </div>

        {/* Name */}
        <div style={{ color: isCurrent ? '#ccc' : '#e67e22', fontSize: '0.87rem', fontWeight: 'bold', lineHeight: 1.2 }}>{name}</div>

        {/* AP badge + element */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
          <span style={{ background: apReduced ? '#d35400' : typeStyle.color, color: '#fff', fontSize: '0.73rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '5px' }}>
            {ap} AP{apReduced ? ' ▼' : ''}
          </span>
          {elemStyle && <span style={{ color: elemStyle.color, fontSize: '0.72rem' }}>{elemStyle.icon} {element}</span>}
        </div>

        {/* Description */}
        <div style={{ color: isCurrent ? '#666' : '#888', fontSize: '0.76rem', fontStyle: 'italic', lineHeight: 1.45, borderTop: '1px solid #1a1a2a', paddingTop: '0.35rem' }}>
          {fc.description}
        </div>

        {/* Stat rows */}
        {statChanges.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.18rem', borderTop: '1px solid #1a1a2a', paddingTop: '0.35rem' }}>
            {statChanges.map((c, i) => {
              const val     = isCurrent ? c.before : c.after;
              const changed = !isCurrent;
              return (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: '0.74rem', padding: '3px 6px', borderRadius: '5px',
                  background: changed ? (c.isUnlock ? 'rgba(46,204,113,0.10)' : 'rgba(230,126,34,0.09)') : 'transparent',
                }}>
                  <span style={{ color: changed ? (c.isUnlock ? '#6a9' : '#a87') : '#4a4a5a' }}>{c.label}</span>
                  <span style={{ fontWeight: changed ? 'bold' : 'normal', color: changed ? (c.isUnlock ? '#2ecc71' : '#e67e22') : '#555' }}>
                    {val}
                    {changed && c.isUnlock && ' ✓'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Rank label bottom */}
        <div style={{ marginTop: 'auto', paddingTop: '0.3rem', fontSize: '0.68rem', color: isCurrent ? '#3a3a4a' : '#8a5a2a', textAlign: 'right' }}>{label}</div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }} onClick={onCancel}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        border: `2px solid ${typeStyle.color}40`,
        borderTop: `3px solid ${typeStyle.color}`,
        borderRadius: '16px', padding: '1.75rem',
        width: '600px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto',
        boxShadow: `0 0 50px ${typeStyle.color}30, 0 0 100px rgba(0,0,0,0.7)`,
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.35rem' }}>
          <span style={{ fontSize: '1.75rem', lineHeight: 1 }}>⚒</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#fff' }}>Forge Preview</div>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              {typeStyle.icon} {typeStyle.label} · {(SOURCE_LABELS[fc.source] || fc.source)} · <span style={{ color: '#aaa' }}>{fc.rarity}</span>
            </div>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#777', cursor: 'pointer', fontSize: '1.3rem', lineHeight: 1 }}>✕</button>
        </div>

        {/* Side-by-side card panels */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 28px 1fr', gap: '0.6rem', alignItems: 'center', marginBottom: '1.35rem' }}>
          <CardPanel side="current" />
          <div style={{ textAlign: 'center', color: '#e67e22', fontSize: '1.5rem', fontWeight: 'bold', lineHeight: 1 }}>→</div>
          <CardPanel side="after" />
        </div>

        {/* Final rank note */}
        {fc.forgeRank === 4 && (
          <div style={{ marginBottom: '1rem', padding: '0.55rem 0.75rem', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '8px', fontSize: '0.8rem', color: '#d4af37' }}>
            ★ This is the final rank — the card will reach its maximum potential.
          </div>
        )}

        {/* Token cost bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.65rem 0.85rem', borderRadius: '9px', marginBottom: canAfford ? '1rem' : '0.5rem',
          background: canAfford ? 'rgba(230,126,34,0.1)' : 'rgba(231,76,60,0.08)',
          border: `1px solid ${canAfford ? 'rgba(230,126,34,0.45)' : 'rgba(231,76,60,0.35)'}`,
        }}>
          <span style={{ fontSize: '1.1rem' }}>⚒</span>
          <span style={{ fontSize: '0.88rem', color: '#ddd', flex: 1 }}>
            Cost: <strong style={{ color: canAfford ? '#e67e22' : '#e74c3c' }}>1 Forge Token</strong>
          </span>
          <span style={{ fontSize: '0.82rem', color: '#888' }}>
            You have: <strong style={{ color: canAfford ? '#e67e22' : '#e74c3c' }}>{forgeTokens}</strong>
          </span>
        </div>

        {!canAfford && (
          <div style={{ color: '#c0392b', fontSize: '0.8rem', marginBottom: '0.85rem', textAlign: 'center', lineHeight: 1.5 }}>
            ⚠ Not enough Forge Tokens. At your next level-up, choose the ⚒ Forge Token option instead of a card.
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: '0.7rem',
            background: 'rgba(70,70,70,0.5)', border: '1px solid #444',
            borderRadius: '9px', color: '#ccc', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem',
          }}>← Back</button>
          <button
            onClick={onConfirm}
            disabled={!canAfford || working}
            style={{
              flex: 2, padding: '0.7rem', border: 'none', borderRadius: '9px',
              background: canAfford ? 'linear-gradient(135deg, #e67e22, #d35400)' : '#333',
              color: '#fff', fontWeight: 'bold', fontSize: '0.95rem',
              cursor: canAfford && !working ? 'pointer' : 'not-allowed',
              opacity: working ? 0.7 : 1,
              boxShadow: canAfford ? '0 0 18px rgba(230,126,34,0.4)' : 'none',
              letterSpacing: '0.3px',
            }}
          >
            {working ? '⚒ Forging…' : `⚒ Forge to Rank ${nxtLabel === '★' ? '★ (MAX)' : nxtLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// RANK DOTS HELPER
// ============================================================

const RankDots = ({ rank, size = 10, gap = 3 }) => (
  <div style={{ display: 'flex', gap: `${gap}px`, alignItems: 'center' }}>
    {Array.from({ length: 5 }, (_, i) => (
      <div key={i} style={{
        width: `${size}px`, height: `${size}px`, borderRadius: '50%',
        background: i < rank ? '#e67e22' : '#1e1e1e',
        border: `1px solid ${i < rank ? '#e67e22' : '#3a3a3a'}`,
        flexShrink: 0,
      }} />
    ))}
  </div>
);

// ============================================================
// MAIN DECK MANAGER COMPONENT
// ============================================================

const DeckManager = ({ character, onClose, initialTab = 'library' }) => {  // eslint-disable-line

  // ── Section state ──
  const [activeSection, setActiveSection] = useState(initialTab);

  // ── Card detail ──
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardUpgrades, setCardUpgrades] = useState({});

  // ── Card trashing (v800) ──
  const [trashTokens, setTrashTokens] = useState(0);
  const [trashing,    setTrashing]    = useState(false);

  // ── Loadout tab ──
  const [loadoutData,       setLoadoutData]       = useState(null);
  const [loadoutDraft,      setLoadoutDraft]       = useState([]);
  const [loadoutSaving,     setLoadoutSaving]      = useState(false);
  const [loadoutMsg,        setLoadoutMsg]         = useState(null);
  const [loadoutSearch,     setLoadoutSearch]      = useState('');
  const [loadoutTypeFilter, setLoadoutTypeFilter]  = useState('all');
  const [loadoutSrcFilter,  setLoadoutSrcFilter]   = useState('all');
  const [loadoutSort,       setLoadoutSort]        = useState('default');

  // ── Library tab ──
  const [libFilter,    setLibFilter]    = useState('all');
  const [libSrcFilter, setLibSrcFilter] = useState('all');
  const [libSearch,    setLibSearch]    = useState('');

  const [presetMsg,     setPresetMsg]     = useState(null);
  const [presetWorking, setPresetWorking] = useState(false);
  const [presetNames,   setPresetNames]   = useState({});

  // ── Forge tab ──
  const [forgeData,     setForgeData]     = useState(null);
  const [forgeError,    setForgeError]    = useState(null);
  const [forgeMsg,      setForgeMsg]      = useState(null);
  const [forgeFilter,   setForgeFilter]   = useState('all');
  const [selectedForge, setSelectedForge] = useState(null);
  const [forgeWorking,  setForgeWorking]  = useState(false);

  // ── Floating tooltip (portal, bypasses overflow clipping) ──
  const [tooltip, setTooltip] = useState(null); // { x, y, content }
  const showTip = (e, content) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ x: r.left + r.width / 2, y: r.bottom + 8, content });
  };
  const hideTip = () => setTooltip(null);

  const token = () => localStorage.getItem('token');

  // ────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ────────────────────────────────────────────────────────────────────────

  const fetchLoadout = useCallback(async () => {
    try {
      const res = await fetch('/api/player/loadout', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        setLoadoutData(data);
        setLoadoutDraft(data.deckLoadout || []);
        // Sync card upgrades for forge rank badges
        if (data.cardUpgrades) setCardUpgrades(data.cardUpgrades);
        setTrashTokens(data.trashTokens || 0);
      } else {
        const errData = await res.json().catch(() => ({}));
        setLoadoutData({ _error: errData.error || `Server error ${res.status}` });
      }
    } catch (e) {
      console.warn('fetchLoadout:', e);
      setLoadoutData({ _error: 'Network error — could not reach server' });
    }
  }, []);

  const fetchForge = useCallback(async () => {
    setForgeError(null);
    try {
      const res = await fetch('/api/player/forge', { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) { setForgeError('Failed to load forge data'); return; }
      const data = await res.json();
      setForgeData(data);
      const upg = {};
      (data.forgeable || []).forEach(fc => { if (fc.forgeRank > 0) upg[fc.cardKey] = fc.forgeRank; });
      setCardUpgrades(upg);
    } catch (e) { setForgeError('Network error — could not reach forge'); }
  }, []);

  // Eager load on mount
  useEffect(() => {
    fetchLoadout();
    fetchForge();
  }, [fetchLoadout, fetchForge]);

  // ────────────────────────────────────────────────────────────────────────
  // LOADOUT HANDLERS
  // ────────────────────────────────────────────────────────────────────────

  const handleLoadoutAdd = (poolKey) => {
    if (!loadoutData) return;
    const slots = loadoutData.deckSlots || 10;
    if (loadoutDraft.length >= slots) { setLoadoutMsg('❌ No free slots.'); return; }
    setLoadoutMsg(null);
    setLoadoutDraft(p => [...p, poolKey]);
  };

  const handleLoadoutRemove = (idx) => {
    setLoadoutMsg(null);
    setLoadoutDraft(p => p.filter((_, i) => i !== idx));
  };

  const handleLoadoutSave = async () => {
    try {
      setLoadoutSaving(true); setLoadoutMsg(null);
      const res = await fetch('/api/player/loadout', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ loadout: loadoutDraft }),
      });
      const data = await res.json();
      if (res.ok) { setLoadoutData(p => ({ ...p, deckLoadout: loadoutDraft })); setLoadoutMsg('✅ Loadout saved!'); }
      else { setLoadoutMsg(`❌ ${data.error || 'Save failed'}`); }
    } catch { setLoadoutMsg('❌ Network error'); }
    finally { setLoadoutSaving(false); }
  };

  // PRESET HANDLERS
  // ──────────────────────────────────────────────────────────

  const handlePresetSave = async (slotId) => {
    try {
      setPresetWorking(true); setPresetMsg(null);
      const name = (presetNames[slotId] || '').trim() || `Preset ${slotId}`;
      const res = await fetch(`/api/player/presets/${slotId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ name, loadout: loadoutDraft }),
      });
      const data = await res.json();
      if (res.ok) {
        setLoadoutData(p => ({ ...p, deckPresets: data.presets }));
        setPresetMsg(`✅ Saved to “${name}”`);
      } else setPresetMsg(`❌ ${data.error || 'Save failed'}`);
    } catch { setPresetMsg('❌ Network error'); }
    finally { setPresetWorking(false); }
  };

  const handlePresetLoad = async (slotId) => {
    try {
      setPresetWorking(true); setPresetMsg(null);
      const res = await fetch(`/api/player/loadout/preset/${slotId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (res.ok) {
        setLoadoutDraft(data.deckLoadout || []);
        setLoadoutData(p => ({ ...p, deckLoadout: data.deckLoadout || [] }));
        setPresetMsg('✅ Deck loaded');
      } else setPresetMsg(`❌ ${data.error || 'Load failed'}`);
    } catch { setPresetMsg('❌ Network error'); }
    finally { setPresetWorking(false); }
  };

  // FORGE HANDLERS
  // ──────────────────────────────────────────────────────────

  const handleForgeConfirm = async () => {
    if (!selectedForge) return;
    try {
      setForgeWorking(true); setForgeMsg(null);
      const res = await fetch('/api/player/forge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ cardKey: selectedForge.cardKey }),
      });
      const data = await res.json();
      if (res.ok) {
        setForgeMsg(`⚒ ${data.message || 'Card forged!'}`);
        setSelectedForge(null);
        fetchForge();
        fetchLoadout(); // refresh card names + pool after rank-up
      } else {
        setForgeMsg(`❌ ${data.error || 'Forge failed'}`);
        setSelectedForge(null);
      }
    } catch { setForgeMsg('❌ Network error'); setSelectedForge(null); }
    finally { setForgeWorking(false); }
  };

  const handleGoToForge = (cardKey) => {
    setActiveSection('forge');
    setForgeMsg(`Looking for "${cardKey}" in the forge list…`);
    if (!forgeData) fetchForge();
  };

  // Permanently remove a card from the library (or unlearn a spell).
  const handleTrashCard = async (trashKey) => {
    setTrashing(true);
    try {
      const res = await fetch('/api/player/trash-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ cardKey: trashKey }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoadoutMsg(`❌ ${data.error || 'Could not trash that card'}`);
      } else {
        setTrashTokens(data.trashTokens ?? 0);
        setLoadoutMsg(`🗑 ${data.note}`);
        setSelectedCard(null);
        await fetchLoadout();
        if (forgeData) fetchForge();
      }
    } catch {
      setLoadoutMsg('❌ Network error');
    } finally {
      setTrashing(false);
    }
  };

  // ──────────────────────────────────────────────────────────
  // ────────────────────────────────────────────────────────────────────────
  // DERIVED DATA
  // ────────────────────────────────────────────────────────────────────────

  // Forge filter
  const forgeFiltered = (forgeData?.forgeable || []).filter(fc => {
    if (forgeFilter === 'unforged') return fc.forgeRank === 0;
    if (forgeFilter === 'forged')   return fc.forgeRank > 0;
    return true;
  });

  // Loadout pool derived data
  const poolCards   = loadoutData?.pool   || [];
  const fixedCards  = loadoutData?.fixed  || [];

  const poolCardMap = {};
  poolCards.forEach(c => { if (c._poolKey) poolCardMap[c._poolKey] = c; });

  const loadoutSlotCounts = {};
  loadoutDraft.forEach(k => { loadoutSlotCounts[k] = (loadoutSlotCounts[k] || 0) + 1; });

  const poolTypes = [...new Set(poolCards.map(c => c.type))];
  const poolSrcs  = [...new Set(poolCards.map(c => c.source))].filter(s => ['class','level_up','seed'].includes(s));

  const filteredPool = poolCards.filter(c => {
    if (loadoutSrcFilter !== 'all' && c.source !== loadoutSrcFilter) return false;
    if (loadoutTypeFilter !== 'all' && c.type !== loadoutTypeFilter) return false;
    if (loadoutSearch && !c.name.toLowerCase().includes(loadoutSearch.toLowerCase())) return false;
    return true;
  });
  const sortedPool = sortCards(filteredPool, loadoutSort);

  // Highest-tier (mp) spell per element in the pool — used to flag "outgrown" low tiers.
  const bestSpellByElement = {};
  for (const c of poolCards) {
    const el = c._spellMeta?.element;
    const mp = c._spellMeta?.mp;
    if (!el || typeof mp !== 'number') continue;
    if (!bestSpellByElement[el] || mp > bestSpellByElement[el].mp) bestSpellByElement[el] = { mp, name: c.name };
  }

  // Library derived data
  const allLibCards = [...poolCards, ...fixedCards];
  const libTypes    = [...new Set(allLibCards.map(c => c.type))];
  const libSrcs     = [...new Set(allLibCards.map(c => c.source))];
  const filteredLib = allLibCards.filter(c => {
    if (libFilter    !== 'all' && c.type   !== libFilter)    return false;
    if (libSrcFilter !== 'all' && c.source !== libSrcFilter) return false;
    if (libSearch && !c.name.toLowerCase().includes(libSearch.toLowerCase())) return false;
    return true;
  });

  // Header stat: total active cards when this loadout is used
  const totalPoolInCombat = loadoutDraft.length > 0
    ? loadoutDraft.length
    : poolCards.reduce((s, c) => s + (c._maxCopies || 1), 0);
  const totalInCombat = fixedCards.length + totalPoolInCombat;

  // ────────────────────────────────────────────────────────────────────────
  // TABS DEFINITION
  // ────────────────────────────────────────────────────────────────────────

  const SECTIONS = [
    { id: 'library', icon: '📚', label: 'Library' },
    { id: 'loadout', icon: '🎮', label: 'Loadout' },
    { id: 'presets', icon: '🎒', label: 'Decks' },
    {
      id: 'forge', icon: '⚒',
      label: forgeData
        ? `Forge${forgeData.forgeTokens > 0 ? ` (${forgeData.forgeTokens} 🔑)` : ''}`
        : 'Forge',
    },
  ];


  // RENDER
  // ──────────────────────────────────────────────────────────

  return (
    <div className="dm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dm-container" style={{ maxWidth: '980px', width: '96vw' }}>

        {/* ── Header ── */}
        <div className="dm-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 className="dm-title" style={{ margin: 0 }}>⚙️ Deck</h2>
              <a
                href="/combat-guide.html"
                target="_blank"
                rel="noopener noreferrer"
                title="Open Combat & Deck-Building Guide"
                style={{
                  fontSize: '0.75rem',
                  color: '#80b0ff',
                  textDecoration: 'none',
                  background: 'rgba(100,140,255,0.1)',
                  border: '1px solid rgba(100,140,255,0.25)',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  marginTop: '2px',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={e => { e.target.style.background = 'rgba(100,140,255,0.2)'; e.target.style.borderColor = 'rgba(100,140,255,0.5)'; }}
                onMouseLeave={e => { e.target.style.background = 'rgba(100,140,255,0.1)'; e.target.style.borderColor = 'rgba(100,140,255,0.25)'; }}
              >📖 Guide</a>
            </div>
            <div style={{ color: '#666', fontSize: '0.75rem' }}>
              {character?.name}{totalInCombat > 0 ? ` · ⚔️ ${totalInCombat} combat cards` : ''}
              {forgeData?.forgeTokens > 0 && <span style={{ color: '#e67e22', marginLeft: '0.5rem' }}>· ⚒ {forgeData.forgeTokens} forge token{forgeData.forgeTokens !== 1 ? 's' : ''}</span>}
            </div>
          </div>
          <button className="dm-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Section tabs ── */}
        <div className="dm-tabs" style={{ background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid #1e1e2e', padding: '0 0.75rem', flexShrink: 0 }}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              className={`dm-tab ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
              style={{ fontSize: '0.83rem', padding: '0.55rem 0.9rem' }}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════ */}
        {/* LIBRARY TAB                                      */}
        {/* ══════════════════════════════════════════════════ */}
        {activeSection === 'library' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {!loadoutData ? (
              <div className="dm-loading">Loading library…</div>
            ) : loadoutData._error ? (
              <div className="dm-error">⚠ {loadoutData._error} <button onClick={fetchLoadout} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>Retry</button></div>
            ) : (
              <>
                {/* Filter bar */}
                <div style={{ padding: '0.6rem 1rem 0.45rem', borderBottom: '1px solid #1e1e2e', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '0.88rem' }}>
                      📚 Card Library — {allLibCards.length} cards
                    </span>
                    <span className="dm-help-icon" onMouseEnter={e => showTip(e, <>
                      <div style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: '0.3rem' }}>📚 Your Card Library</div>
                      <p style={{ margin: '0 0 0.4rem' }}>Every card available to your character — both fixed and chooseable. Click any card to inspect it in detail.</p>
                      <div style={{ color: '#888', borderTop: '1px solid #1e1e2e', paddingTop: '0.35rem', marginTop: '0.35rem' }}>
                        <div><strong style={{ color: '#ccc' }}>🔒 Fixed cards</strong> always enter combat (equipment, race, skills…)</div>
                        <div style={{ marginTop: '0.25rem' }}><strong style={{ color: '#ccc' }}>🎮 Pool cards</strong> are chooseable — use the <em>Loadout</em> tab to decide which ones fight</div>
                      </div>
                    </>)} onMouseLeave={hideTip}>?</span>
                    <span style={{ color: '#555', fontSize: '0.72rem', marginLeft: 'auto' }}>Click any card for details</span>
                  </div>
                  <input
                    type="text" value={libSearch} onChange={e => setLibSearch(e.target.value)}
                    placeholder="Search cards…"
                    style={{ width: '100%', padding: '0.35rem 0.65rem', borderRadius: '6px', border: '1px solid #2a2a3a', background: '#111', color: '#ccc', fontSize: '0.8rem', marginBottom: '0.35rem', boxSizing: 'border-box' }}
                  />
                  {/* Source filter */}
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.28rem' }}>
                    {[['all', 'All'], ...libSrcs.map(s => [s, `${SOURCE_LABELS[s] || s}`])].map(([id, label]) => (
                      <button key={id} onClick={() => setLibSrcFilter(id)} style={{
                        padding: '0.18rem 0.55rem', borderRadius: '5px', fontSize: '0.72rem', border: 'none', cursor: 'pointer',
                        background: libSrcFilter === id ? '#d4af37' : '#1a1a2a',
                        color: libSrcFilter === id ? '#111' : '#777',
                        fontWeight: libSrcFilter === id ? 'bold' : 'normal',
                      }}>{SOURCE_ICONS[id] || ''} {label}</button>
                    ))}
                  </div>
                  {/* Type filter */}
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    {['all', ...libTypes].map(type => {
                      const ts = type === 'all' ? null : (CARD_TYPE_STYLE[type] || CARD_TYPE_STYLE.special);
                      const cnt = type === 'all' ? allLibCards.length : allLibCards.filter(c => c.type === type).length;
                      const active = libFilter === type;
                      return (
                        <button key={type} onClick={() => setLibFilter(type)} style={{
                          padding: '0.18rem 0.5rem', borderRadius: '5px', fontSize: '0.72rem', border: 'none', cursor: 'pointer',
                          background: active ? (ts?.color || '#888') : '#1a1a2a',
                          color: active ? '#fff' : '#777', fontWeight: active ? 'bold' : 'normal',
                        }}>
                          {ts ? `${ts.icon} ${ts.label}` : 'All'} ({cnt})
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Card grid */}
                <div className="dm-card-grid" style={{ overflowY: 'auto', flex: 1, padding: '0.5rem 1rem 1rem' }}>
                  {filteredLib.map((card, i) => {
                    const ts  = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
                    const rs  = RARITY_STYLE[card.rarity]  || RARITY_STYLE.common;
                    const rank = (cardUpgrades || {})[card.cardKey] || 0;
                    const element  = card._spellMeta?.element || card.effects?.magicDamage?.element;
                    const elemStyle = element ? ELEMENT_STYLE[element] : null;
                    return (
                      <div key={`lib-${card.cardKey || card.name}-${i}`}
                        className={`dm-card ${card.type} rarity-${card.rarity}`}
                        style={{ borderColor: rs.border, boxShadow: rs.glow, cursor: 'pointer', position: 'relative' }}
                        onClick={() => setSelectedCard(card)}
                        title="Click for details"
                      >
                        {card.rarity === 'legendary' && <div className="dm-legendary-sparkle" />}
                        <div className="dm-card-ap" style={{ background: ts.color }}>{card.apCost}</div>
                        {rank > 0 && (
                          <div style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(230,126,34,0.9)', borderRadius: '4px', padding: '1px 5px', fontSize: '0.6rem', fontWeight: 'bold', color: '#fff', zIndex: 2 }}>
                            ⚒{FORGE_RANK_LABELS[rank - 1]}
                          </div>
                        )}
                        <div className="dm-card-header">
                          <span className="dm-card-type-icon">{ts.icon}</span>
                          <span className="dm-card-name">{card.name}</span>
                        </div>
                        {elemStyle && <div className="dm-card-element" style={{ color: elemStyle.color }}>{elemStyle.icon} {element}</div>}
                        {card._spellMeta?.mp > 0 && <div className="dm-card-mp">{card._spellMeta.mp} MP</div>}
                        <div className="dm-card-desc">{card.description}</div>
                        {card.keywords?.length > 0 && (
                          <div className="dm-card-keywords">
                            {card.keywords.map((kw, ki) => {
                              const kwStyle = KEYWORD_STYLE[kw] || { color: '#aaa', bg: 'rgba(150,150,150,0.2)' };
                              return <span key={ki} className="dm-keyword" style={{ color: kwStyle.color, background: kwStyle.bg }}>{kw}</span>;
                            })}
                          </div>
                        )}
                        <div style={{ marginTop: 'auto', paddingTop: '0.35rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.62rem' }}>
                          <span style={{ color: '#3a3a4a' }}>{SOURCE_ICONS[card.source]} {SOURCE_LABELS[card.source] || card.source}</span>
                          <span style={{ color: '#2a4a3a' }}>🔍 inspect</span>
                        </div>
                      </div>
                    );
                  })}
                  {filteredLib.length === 0 && (
                    <div style={{ color: '#555', fontSize: '0.85rem', gridColumn: '1/-1', padding: '2rem', textAlign: 'center' }}>
                      {libSearch || libFilter !== 'all' || libSrcFilter !== 'all' ? 'No cards match this filter.' : 'No cards found.'}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/* ══════════════════════════════════════════════════ */}
        {/* LOADOUT TAB                                      */}
        {/* ══════════════════════════════════════════════════ */}
        {activeSection === 'loadout' && (
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'hidden' }}>
            {!loadoutData ? (
              <div className="dm-loading">Loading loadout…</div>
            ) : loadoutData._error ? (
              <div className="dm-error">⚠ {loadoutData._error} <button onClick={fetchLoadout} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>Retry</button></div>
            ) : (() => {
              const slots = loadoutData.deckSlots || 10;

              // Fixed cards grouped by source (always in combat)
              const FIXED_SRC_ORDER = ['equipment', 'skill_tree', 'advanced_class', 'race', 'alignment', 'set_bonus'];
              const fixedBySrc = {};
              for (const c of fixedCards) {
                const s = c.source || 'other';
                if (!fixedBySrc[s]) fixedBySrc[s] = [];
                fixedBySrc[s].push(c);
              }
              const activeFixedSrcs = FIXED_SRC_ORDER.filter(s => fixedBySrc[s]?.length > 0);

              // All pool cards always-in-combat count (when draft is empty)
              const allPoolCount = poolCards.reduce((s, c) => s + (c._maxCopies || 1), 0);

              return (
                <>
                  {/* ── LEFT PANEL: COMBAT DECK + LOADOUT SLOTS ── */}
                  <div style={{ width: '255px', flexShrink: 0, borderRight: '1px solid #1e1e2e', background: 'rgba(0,0,0,0.15)', overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '0.6rem 0.7rem 1rem', gap: '0.4rem' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', flexShrink: 0 }}>
                      <span style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '0.82rem' }}>
                        ⚔️ {totalInCombat} combat cards
                      </span>
                      <span className="dm-help-icon" onMouseEnter={e => showTip(e, <>
                        <div style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: '0.3rem' }}>⚔️ Total Combat Cards</div>
                        <p style={{ margin: '0 0 0.4rem' }}>Cards that enter every fight: your fixed cards plus whichever pool cards are currently active.</p>
                        <div style={{ color: '#888', borderTop: '1px solid #1e1e2e', paddingTop: '0.35rem', marginTop: '0.35rem' }}>
                          <div>If <strong style={{ color: '#ccc' }}>Picks</strong> slots are empty, <em>all</em> pool cards are active.</div>
                          <div style={{ marginTop: '0.2rem' }}>Fill Picks to control exactly which pool cards join the fight.</div>
                        </div>
                      </>)} onMouseLeave={hideTip}>?</span>
                      <button onClick={handleLoadoutSave} disabled={loadoutSaving} style={{
                        marginLeft: 'auto', padding: '0.25rem 0.7rem', background: '#d4af37',
                        border: 'none', borderRadius: '6px', color: '#111',
                        fontWeight: 'bold', cursor: loadoutSaving ? 'not-allowed' : 'pointer', fontSize: '0.72rem',
                      }}>
                        {loadoutSaving ? 'Saving…' : '💾 Save'}
                      </button>
                    </div>
                    {loadoutMsg && (
                      <div style={{ color: loadoutMsg.startsWith('❌') ? '#e74c3c' : '#2ecc71', fontSize: '0.72rem', flexShrink: 0 }}>{loadoutMsg}</div>
                    )}

                    {/* Fixed cards chips by source */}
                    <div style={{ borderTop: '1px solid #1e1e2e', paddingTop: '0.4rem', flexShrink: 0 }}>
                      <div style={{ color: '#444', fontSize: '0.6rem', marginBottom: '0.28rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        Always in combat
                        <span className="dm-help-icon" style={{ fontSize: '0.55rem', width: '12px', height: '12px', textTransform: 'none' }} onMouseEnter={e => showTip(e, <>
                          <div style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: '0.3rem' }}>🔒 Fixed Cards</div>
                          <p style={{ margin: '0 0 0.4rem' }}>These cards always enter combat — they can't be removed through the Loadout.</p>
                          <div style={{ color: '#888', borderTop: '1px solid #1e1e2e', paddingTop: '0.35rem', marginTop: '0.35rem' }}>
                            <div>Sources: <strong style={{ color: '#ccc' }}>Equipment, Race, Alignment, Skill Tree, Advanced Class, Set Bonuses</strong></div>
                            <div style={{ marginTop: '0.2rem', fontSize: '0.65rem', color: '#555' }}>Manage these by changing gear &amp; skill choices.</div>
                          </div>
                        </>)} onMouseLeave={hideTip}>?</span>
                      </div>
                      {activeFixedSrcs.map(src => (
                        <div key={src} style={{ marginBottom: '0.3rem' }}>
                          <div style={{ color: '#3a3a4a', fontSize: '0.59rem', marginBottom: '0.12rem' }}>{SOURCE_ICONS[src]} {SOURCE_LABELS[src]}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.18rem' }}>
                            {fixedBySrc[src].map((card, ci) => {
                              const ts = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
                              return (
                                <div key={ci}
                                  onClick={() => setSelectedCard(card)}
                                  title={`${card.name} · ${card.apCost}AP · ${card.description}`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '2px 6px', borderRadius: '5px', fontSize: '0.63rem',
                                    background: 'rgba(22,22,35,0.95)', border: `1px solid ${ts.color}44`,
                                    color: '#aaa', cursor: 'pointer', transition: 'border-color 0.15s',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.borderColor = ts.color}
                                  onMouseLeave={e => e.currentTarget.style.borderColor = `${ts.color}44`}
                                >
                                  <span style={{ color: ts.color }}>{ts.icon}</span>
                                  {card.name}
                                  <span style={{ color: '#3a3a4a', fontSize: '0.56rem', marginLeft: '2px' }}>{card.apCost}AP</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Loadout slot list */}
                    <div style={{ borderTop: '1px solid #1e1e2e', paddingTop: '0.4rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.28rem', flexWrap: 'wrap', flexShrink: 0 }}>
                        <span style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '0.75rem' }}>
                          🎮 Picks — {loadoutDraft.length}/{slots}
                        </span>
                        {loadoutDraft.length === 0 && (
                          <span style={{ color: '#3a3a3a', fontSize: '0.62rem' }}>random fill</span>
                        )}
                        {/* Help tooltip */}
                        <span className="dm-help-icon" onMouseEnter={e => showTip(e, <>
                          <div style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: '0.3rem' }}>🎮 How Loadout Picks Work</div>
                          <p style={{ margin: '0 0 0.4rem' }}>Pick cards from the right panel to fill your slots. Only the cards you pick will enter combat. If you leave your loadout empty, a <em>random</em> selection will be used each fight.</p>
                          <div style={{ color: '#888', borderTop: '1px solid #1e1e2e', paddingTop: '0.35rem', marginTop: '0.35rem' }}>
                            <div style={{ color: '#c0a040', fontWeight: 'bold', marginBottom: '0.2rem' }}>➕ Expand your slots:</div>
                            <div>• <strong style={{ color: '#ccc' }}>Skill Tree</strong> trunk perks grant +2 or +4 slots</div>
                            <div>• <strong style={{ color: '#ccc' }}>Advanced Class</strong> "Expanded Loadout" perks grant +2, +4, or +6 slots</div>
                            <div style={{ color: '#555', marginTop: '0.3rem', fontSize: '0.65rem' }}>Both stack — max possible: 20 slots</div>
                          </div>
                        </>)} onMouseLeave={hideTip}>?</span>
                      </div>
                      {loadoutDraft.length === 0 && (
                        <div style={{ color: '#333', fontSize: '0.63rem', fontStyle: 'italic', marginBottom: '0.35rem', flexShrink: 0 }}>Fill slots to choose — empty = random selection each fight</div>
                      )}
                      {/* Vertical slot rows */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {Array.from({ length: slots }, (_, i) => {
                          const key  = loadoutDraft[i];
                          const card = key ? poolCardMap[key] : null;
                          const ts   = card ? (CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special) : null;
                          return (
                            <div key={i}
                              onClick={() => key && handleLoadoutRemove(i)}
                              title={card ? `Remove: ${card.name}` : `Empty slot ${i + 1}`}
                              style={{
                                height: '30px', borderRadius: '6px',
                                border: key ? `2px solid ${ts?.color || '#d4af37'}` : '2px dashed #252535',
                                background: key ? 'rgba(20,20,32,0.97)' : 'rgba(12,12,18,0.3)',
                                display: 'flex', alignItems: 'center', overflow: 'hidden',
                                cursor: key ? 'pointer' : 'default',
                              }}
                            >
                              {key && ts ? (
                                <>
                                  <div style={{ background: ts.color, width: '26px', flexShrink: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ fontSize: '0.68rem', color: '#fff' }}>{ts.icon}</span>
                                  </div>
                                  <div style={{ padding: '0 6px', fontSize: '0.63rem', color: '#ddd', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {card?.name || key.split(':')[1] || key}
                                  </div>
                                  <div style={{ paddingRight: '5px', fontSize: '0.6rem', color: 'rgba(255,255,255,0.28)', flexShrink: 0 }}>✕</div>
                                </>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#252535', fontSize: '0.63rem' }}>{i + 1}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* ── RIGHT PANEL: CARD POOL ── */}
                  <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '0.5rem 1rem 0', flexShrink: 0 }}>
                      <input
                        type="text"
                        value={loadoutSearch}
                        onChange={e => setLoadoutSearch(e.target.value)}
                        placeholder="Search cards…"
                        style={{
                          width: '100%', padding: '0.35rem 0.65rem', borderRadius: '6px',
                          border: '1px solid #2a2a3a', background: '#111', color: '#ccc',
                          fontSize: '0.8rem', marginBottom: '0.35rem', boxSizing: 'border-box',
                        }}
                      />
                      {/* Source filter */}
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.28rem', alignItems: 'center' }}>
                        <span className="dm-help-icon" onMouseEnter={e => showTip(e, <>
                          <div style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: '0.3rem' }}>🃏 Card Sources</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                            <div><strong style={{ color: '#a78bfa' }}>Class Cards</strong> — base class abilities, granted automatically</div>
                            <div><strong style={{ color: '#60a5fa' }}>Level-Up Picks</strong> — cards you personally chose when leveling up</div>
                            <div><strong style={{ color: '#34d399' }}>Spells / Seeds</strong> — spells unlocked by your equipped seeds &amp; items</div>
                          </div>
                          <div style={{ color: '#555', borderTop: '1px solid #1e1e2e', paddingTop: '0.3rem', marginTop: '0.35rem', fontSize: '0.65rem' }}>All sources compete for the same Picks slots.</div>
                        </>)} onMouseLeave={hideTip}>?</span>
                        {[['all', 'All'], ...poolSrcs.map(s => [s, SOURCE_LABELS[s] || s])].map(([id, label]) => (
                          <button key={id} onClick={() => setLoadoutSrcFilter(id)} style={{
                            padding: '0.18rem 0.55rem', borderRadius: '5px', fontSize: '0.72rem', border: 'none', cursor: 'pointer',
                            background: loadoutSrcFilter === id ? '#d4af37' : '#1a1a2a',
                            color: loadoutSrcFilter === id ? '#111' : '#777',
                            fontWeight: loadoutSrcFilter === id ? 'bold' : 'normal',
                          }}>
                            {SOURCE_ICONS[id] || ''} {label}
                          </button>
                        ))}
                      </div>
                      {/* Type filter */}
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                        {['all', ...poolTypes].map(type => {
                          const ts = type === 'all' ? null : (CARD_TYPE_STYLE[type] || CARD_TYPE_STYLE.special);
                          const count = type === 'all' ? poolCards.length : poolCards.filter(c => c.type === type).length;
                          const active = loadoutTypeFilter === type;
                          return (
                            <button key={type} onClick={() => setLoadoutTypeFilter(type)} style={{
                              padding: '0.18rem 0.5rem', borderRadius: '5px', fontSize: '0.72rem',
                              border: 'none', cursor: 'pointer',
                              background: active ? (ts?.color || '#888') : '#1a1a2a',
                              color: active ? '#fff' : '#777',
                              fontWeight: active ? 'bold' : 'normal',
                            }}>
                              {ts ? `${ts.icon} ${ts.label}` : 'All'} ({count})
                            </button>
                          );
                        })}
                      </div>
                      {/* Sort — helps find the strongest spells fast */}
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.35rem', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.68rem', color: '#666' }}>Sort:</span>
                        {[['default', 'Default'], ['tier', 'Tier'], ['damage', 'Damage'], ['seed', 'Seed']].map(([id, label]) => (
                          <button key={id} onClick={() => setLoadoutSort(id)} style={{
                            padding: '0.18rem 0.5rem', borderRadius: '5px', fontSize: '0.72rem', border: 'none', cursor: 'pointer',
                            background: loadoutSort === id ? '#5b8def' : '#1a1a2a',
                            color: loadoutSort === id ? '#fff' : '#777',
                            fontWeight: loadoutSort === id ? 'bold' : 'normal',
                          }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Card grid */}
                    <div className="dm-card-grid" style={{ overflowY: 'auto', flex: 1, padding: '0.25rem 1rem 1rem' }}>
                      {sortedPool.map((card, i) => {
                        const pk        = card._poolKey;
                        const slotted   = loadoutSlotCounts[pk] || 0;
                        const maxCopies = card._maxCopies || 1;
                        const canAdd    = slotted < maxCopies && loadoutDraft.length < slots;
                        const ts        = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
                        const rs        = RARITY_STYLE[card.rarity]  || RARITY_STYLE.common;
                        const rank      = (loadoutData.cardUpgrades || {})[card.cardKey] || 0;
                        const element   = card._spellMeta?.element || card.effects?.magicDamage?.element;
                        const elemStyle = element ? ELEMENT_STYLE[element] : null;
                        const srcLabel  = SOURCE_LABELS[card.source] || card.source;
                        const bestSpell = element ? bestSpellByElement[element] : null;
                        const outgrown  = bestSpell && typeof card._spellMeta?.mp === 'number' && card._spellMeta.mp < bestSpell.mp;
                        return (
                          <div key={`${pk}-${i}`}
                            className={`dm-card ${card.type} rarity-${card.rarity}`}
                            style={{
                              borderColor: canAdd ? rs.border : '#252525',
                              boxShadow:   canAdd ? rs.glow   : 'none',
                              opacity:     canAdd ? 1 : 0.45,
                              cursor:      canAdd ? 'pointer' : 'not-allowed',
                              position: 'relative',
                            }}
                            onClick={() => canAdd && handleLoadoutAdd(pk)}
                            title={canAdd ? `Add "${card.name}" to loadout` : slotted >= maxCopies ? 'Max copies already slotted' : 'Loadout is full'}
                          >
                            {card.rarity === 'legendary' && canAdd && <div className="dm-legendary-sparkle" />}
                            <div className="dm-card-ap" style={{ background: canAdd ? ts.color : '#333' }}>{card.apCost}</div>
                            {rank > 0 && (
                              <div style={{
                                position: 'absolute', top: '4px', right: '4px',
                                background: canAdd ? 'rgba(230,126,34,0.9)' : 'rgba(80,55,15,0.6)',
                                borderRadius: '4px', padding: '1px 5px',
                                fontSize: '0.6rem', fontWeight: 'bold', color: '#fff', zIndex: 2,
                              }}>⚒{FORGE_RANK_LABELS[rank - 1]}</div>
                            )}
                            {maxCopies > 1 && (
                              <div style={{
                                position: 'absolute', top: rank > 0 ? '22px' : '4px', right: '4px',
                                background: 'rgba(52,152,219,0.85)', borderRadius: '4px',
                                padding: '1px 5px', fontSize: '0.6rem', fontWeight: 'bold', color: '#fff', zIndex: 2,
                              }}>×{maxCopies} owned</div>
                            )}
                            {slotted > 0 && (
                              <div style={{
                                position: 'absolute', top: [rank > 0, maxCopies > 1].filter(Boolean).length * 18 + 4 + 'px', right: '4px',
                                background: 'rgba(212,175,55,0.85)', borderRadius: '4px',
                                padding: '1px 5px', fontSize: '0.6rem', fontWeight: 'bold', color: '#111', zIndex: 2,
                              }}>📌×{slotted}</div>
                            )}
                            <div className="dm-card-header">
                              <span className="dm-card-type-icon">{ts.icon}</span>
                              <span className="dm-card-name">{card.name}</span>
                            </div>
                            {elemStyle && <div className="dm-card-element" style={{ color: elemStyle.color }}>{elemStyle.icon} {element}</div>}
                            {card._spellMeta?.mp > 0 && <div className="dm-card-mp">{card._spellMeta.mp} MP</div>}
                            {outgrown && (
                              <div title={`A stronger ${element} spell is available: ${bestSpell.name}`}
                                style={{ fontSize: '0.6rem', color: '#e0a06a', fontWeight: 'bold', margin: '1px 0' }}>
                                ⬆ Outgrown · {bestSpell.name} available
                              </div>
                            )}
                            <div className="dm-card-desc">{card.description}</div>
                            {card.keywords?.length > 0 && (
                              <div className="dm-card-keywords">
                                {card.keywords.map((kw, ki) => {
                                  const kwStyle = KEYWORD_STYLE[kw] || { color: '#aaa', bg: 'rgba(150,150,150,0.2)' };
                                  return <span key={ki} className="dm-keyword" style={{ color: kwStyle.color, background: kwStyle.bg }}>{kw}</span>;
                                })}
                              </div>
                            )}
                            <div style={{ marginTop: 'auto', paddingTop: '0.35rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.62rem' }}>
                              <span style={{ color: '#3a3a4a' }}>{SOURCE_ICONS[card.source]} {srcLabel}</span>
                              <span style={{ color: canAdd ? '#3a9' : '#444' }}>
                                {canAdd
                                  ? maxCopies > 1 ? `＋ Add (${slotted + 1}/${maxCopies})` : '＋ Add'
                                  : slotted >= maxCopies ? '✓ Slotted' : '— Full —'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {filteredPool.length === 0 && (
                        <div style={{ color: '#555', fontSize: '0.85rem', gridColumn: '1/-1', padding: '2rem', textAlign: 'center' }}>
                          {loadoutSearch || loadoutTypeFilter !== 'all' || loadoutSrcFilter !== 'all'
                            ? 'No cards match this filter.'
                            : poolCards.length === 0 ? 'No cards available yet.' : 'No cards match.'}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/* PRESETS TAB                                        */}
        {/* ══════════════════════════════════════════════════ */}
        {activeSection === 'presets' && (
          <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
            {!loadoutData ? (
              <div className="dm-loading">Loading decks…</div>
            ) : (
              <>
                <div style={{
                  marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '10px',
                  background: loadoutData.switchGate?.allowed ? 'rgba(46,204,113,0.1)' : 'rgba(230,126,34,0.1)',
                  border: `1px solid ${loadoutData.switchGate?.allowed ? 'rgba(46,204,113,0.35)' : 'rgba(230,126,34,0.35)'}`,
                  color: loadoutData.switchGate?.allowed ? '#2ecc71' : '#e67e22',
                }}>
                  {loadoutData.switchGate?.allowed
                    ? '✅ You can change your deck here.'
                    : `🔒 ${loadoutData.switchGate?.reason || 'You cannot change your deck here.'}`}
                </div>

                <div style={{ color: '#999', fontSize: '0.85rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                  Save up to five decks and swap between them in a town or at a campfire.
                  Different situations reward different builds — splash cards need several
                  enemies, and a Delve has to be fought with the deck you brought in.
                </div>

                {['1', '2', '3', '4', '5'].map(slotId => {
                  const preset = (loadoutData.deckPresets || {})[slotId];
                  const locked = !loadoutData.switchGate?.allowed || presetWorking;
                  return (
                    <div key={slotId} style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                      padding: '0.7rem 0.85rem', marginBottom: '0.6rem', borderRadius: '8px',
                      background: 'rgba(30,30,30,0.5)', border: '1px solid #2a2a2a',
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>🎒</span>
                      <input
                        type="text"
                        value={presetNames[slotId] ?? (preset?.name || '')}
                        placeholder={`Deck ${slotId}`}
                        onChange={e => setPresetNames(p => ({ ...p, [slotId]: e.target.value }))}
                        style={{
                          flex: 1, background: 'rgba(0,0,0,0.35)', border: '1px solid #333',
                          borderRadius: '6px', color: '#ddd', padding: '0.35rem 0.5rem',
                        }}
                      />
                      <span style={{ color: '#777', fontSize: '0.8rem', minWidth: '4.5rem' }}>
                        {preset ? `${(preset.loadout || []).length} cards` : 'empty'}
                      </span>
                      <button
                        onClick={() => handlePresetSave(slotId)}
                        disabled={locked}
                        style={{ cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.4 : 1 }}
                      >
                        Save current
                      </button>
                      <button
                        onClick={() => handlePresetLoad(slotId)}
                        disabled={locked || !preset}
                        style={{ cursor: (locked || !preset) ? 'not-allowed' : 'pointer', opacity: (locked || !preset) ? 0.4 : 1 }}
                      >
                        Equip
                      </button>
                    </div>
                  );
                })}

                {presetMsg && (
                  <div style={{ marginTop: '0.75rem', color: presetMsg.startsWith('✅') ? '#2ecc71' : '#e74c3c' }}>
                    {presetMsg}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/* FORGE TAB                                         */}
        {/* ══════════════════════════════════════════════════ */}
        {activeSection === 'forge' && (
          <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
            {!forgeData && !forgeError ? (
              <div className="dm-loading">Loading forge data…</div>
            ) : forgeError ? (
              <div className="dm-error">⚠ {forgeError} <button onClick={fetchForge} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>Retry</button></div>
            ) : (
              <>
                {/* Token banner */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1rem',
                  padding: '0.75rem 1rem', borderRadius: '10px',
                  background: forgeData.forgeTokens > 0 ? 'rgba(230,126,34,0.1)' : 'rgba(30,30,30,0.5)',
                  border: `1px solid ${forgeData.forgeTokens > 0 ? 'rgba(230,126,34,0.35)' : '#2a2a2a'}`,
                }}>
                  <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>⚒</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ color: forgeData.forgeTokens > 0 ? '#e67e22' : '#666', fontWeight: 'bold', fontSize: '1rem' }}>
                        {forgeData.forgeTokens > 0
                          ? `${forgeData.forgeTokens} Forge Token${forgeData.forgeTokens !== 1 ? 's' : ''} available`
                          : 'No Forge Tokens'}
                      </div>
                      <span className="dm-help-icon" style={{ borderColor: '#5a3a1a', color: '#7a5a2a' }} onMouseEnter={e => showTip(e, <>
                        <div style={{ color: '#e67e22', fontWeight: 'bold', marginBottom: '0.3rem' }}>⚒ Forge Tokens</div>
                        <p style={{ margin: '0 0 0.4rem' }}>Spend a token to permanently <strong style={{ color: '#ccc' }}>upgrade a card's rank</strong>: I → II → III → IV → ★</p>
                        <div style={{ color: '#888', borderTop: '1px solid #1e1e2e', paddingTop: '0.35rem', marginTop: '0.35rem' }}>
                          <div><strong style={{ color: '#e67e22' }}>Earning tokens:</strong> at level-up, choose <em>⚒ Forge Token</em> instead of a card pick</div>
                          <div style={{ marginTop: '0.2rem', fontSize: '0.65rem', color: '#555' }}>Upgrades are permanent — higher-ranked cards have stronger effects.</div>
                        </div>
                      </>)} onMouseLeave={hideTip}>?</span>
                    </div>
                    <div style={{ color: '#666', fontSize: '0.75rem' }}>
                      {forgeData.forgeTokens > 0
                        ? 'Click any card row to preview the upgrade before spending a token.'
                        : 'Earn tokens at level-up by choosing ⚒ Forge Token instead of a new card.'}
                    </div>
                  </div>
                </div>

                {forgeMsg && (
                  <div style={{
                    color: forgeMsg.startsWith('❌') ? '#e74c3c' : '#27ae60',
                    fontSize: '0.85rem', marginBottom: '0.75rem',
                    padding: '0.4rem 0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '7px',
                  }}>{forgeMsg}</div>
                )}

                {/* Forge filter */}
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {[['all', 'All'], ['unforged', 'Unforged'], ['forged', 'Forged']].map(([id, label]) => (
                    <button key={id} onClick={() => setForgeFilter(id)} style={{
                      padding: '0.28rem 0.7rem', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold',
                      border: `1px solid ${forgeFilter === id ? '#e67e22' : '#2a2a2a'}`,
                      background: forgeFilter === id ? 'rgba(230,126,34,0.18)' : 'rgba(25,25,25,0.7)',
                      color: forgeFilter === id ? '#e67e22' : '#666', cursor: 'pointer',
                    }}>{label}</button>
                  ))}
                  <span style={{ marginLeft: 'auto', fontSize: '0.73rem', color: '#444' }}>
                    {forgeFiltered.length} card{forgeFiltered.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Forgeable card list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {forgeFiltered.length === 0 && <div className="dm-empty">No cards match this filter.</div>}
                  {forgeFiltered.map(fc => {
                    const isMax     = fc.forgeRank >= 5;
                    const typeStyle = CARD_TYPE_STYLE[fc.type] || CARD_TYPE_STYLE.special;
                    const canClick  = !isMax && !forgeWorking;
                    return (
                      <div
                        key={fc.cardKey}
                        onClick={() => canClick && setSelectedForge(fc)}
                        title={isMax ? 'Maximum rank ★ reached' : forgeData.forgeTokens < 1 ? 'No forge tokens available' : 'Click to preview upgrade'}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: 'rgba(20,20,28,0.9)',
                          border: `1px solid ${isMax ? '#2a2a2a' : '#2e2e3e'}`,
                          borderLeft: `3px solid ${isMax ? '#e67e22' : typeStyle.color}`,
                          borderRadius: '9px', padding: '0.6rem 0.85rem',
                          cursor: canClick ? 'pointer' : 'default',
                          transition: 'background 0.15s, border-color 0.15s',
                          opacity: isMax ? 0.65 : 1,
                        }}
                      >
                        <span style={{ fontSize: '1.05rem', flexShrink: 0 }}>{typeStyle.icon}</span>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 'bold', color: isMax ? '#777' : '#ddd', fontSize: '0.87rem', marginBottom: '0.18rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {fc.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                            <RankDots rank={fc.forgeRank} size={9} gap={2} />
                            <span style={{ fontSize: '0.7rem', color: isMax ? '#e67e22' : '#555' }}>
                              {isMax
                                ? '★ MAX'
                                : fc.forgeRank === 0
                                  ? 'Unforged'
                                  : `Rank ${FORGE_RANK_LABELS[fc.forgeRank - 1]} → ${FORGE_RANK_LABELS[fc.forgeRank]}`}
                            </span>
                          </div>
                        </div>

                        {isMax ? (
                          <span style={{ fontSize: '0.82rem', color: '#e67e22', fontWeight: 'bold', flexShrink: 0 }}>★ MAX</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                            <span style={{ fontSize: '0.7rem', color: forgeData.forgeTokens < 1 ? '#e74c3c' : '#666' }}>1 🔑</span>
                            <button
                              onClick={e => { e.stopPropagation(); setSelectedForge(fc); }}
                              disabled={forgeWorking || forgeData.forgeTokens < 1}
                              style={{
                                padding: '0.28rem 0.65rem',
                                background: forgeData.forgeTokens < 1 ? '#1e1e1e' : 'linear-gradient(135deg,#e67e22,#d35400)',
                                border: `1px solid ${forgeData.forgeTokens < 1 ? '#2a2a2a' : '#e67e22'}`,
                                borderRadius: '6px', color: forgeData.forgeTokens < 1 ? '#444' : '#fff',
                                fontWeight: 'bold', cursor: forgeData.forgeTokens < 1 ? 'not-allowed' : 'pointer',
                                fontSize: '0.78rem',
                              }}
                            >
                              ⚒ Preview
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

      </div>{/* /dm-container */}

      {/* ── Card detail popup ── */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          cardUpgrades={cardUpgrades}
          trashTokens={trashTokens}
          onTrash={handleTrashCard}
          trashing={trashing}
          onClose={() => setSelectedCard(null)}
          onGoToForge={handleGoToForge}
        />
      )}

      {/* ── Forge preview modal ── */}
      {selectedForge && (
        <ForgePreviewModal
          fc={selectedForge}
          forgeTokens={forgeData?.forgeTokens ?? 0}
          onConfirm={handleForgeConfirm}
          onCancel={() => setSelectedForge(null)}
          working={forgeWorking}
        />
      )}

      {/* ── Floating tooltip portal — renders at body level, never clipped ── */}
      {tooltip && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: tooltip.y,
          left: tooltip.x,
          transform: 'translateX(-50%)',
          width: '245px',
          padding: '0.6rem 0.75rem',
          background: '#0f0f1e',
          border: '1px solid #2a2a4a',
          borderRadius: '8px',
          fontSize: '0.7rem',
          color: '#aaa',
          lineHeight: 1.5,
          zIndex: 99999,
          pointerEvents: 'none',
          boxShadow: '0 4px 20px rgba(0,0,0,0.7)',
        }}>
          {tooltip.content}
        </div>,
        document.body
      )}

    </div>
  );
};

export default DeckManager;
